import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { startWith, switchMap } from 'rxjs';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';

import { ApContentService, ApContentPipe } from '@ap/content';
import { ErrorMessages } from '@ap/form/errors';
import { ApModalService, ModalClosedReason, ModalClosedResult, ModalOptions } from '@ap/modal';
import {
    ApSelectorModalComponent,
    ApSelectorModalOptions,
    ApSelectorOption,
    ApSelectorSelectionType,
    ApSelectorSelectComponent
} from '@ap/selector';

import content from './digipas-selector.content.json';
import { Digipas } from './digipas-selector.model';

@Component({
    selector: 'ap-digipas-selector',
    templateUrl: './digipas-selector.component.html',
    imports: [ApSelectorSelectComponent, ReactiveFormsModule, ApContentPipe]
})
export class ApDigipasSelectorComponent {
    private readonly contentService = inject(ApContentService);
    private readonly modalService = inject(ApModalService);

    /**
     * Required - List of digipas to select from.
     */
    public readonly options = input.required<Digipas[]>();

    /**
     * Optional control input. Passed on to radio buttons
     */
    public readonly control = input<FormControl<Digipas>>(new FormControl());

    constructor() {
        this.contentService.addContentItems(content);

        effect(() => {
            this.setSelectedOptions(this.selectorOptions());
        });

        effect(() => {
            this.setPreselectedValue(this.controlValue());
        });
    }

    protected readonly selectorOptions = computed<ApSelectorOption[]>(() => {
        const options = this.options();
        return options?.map((option) => this.mapSelectorOption(option)) || [];
    });
    protected selectedOptions = signal<ApSelectorOption[]>(undefined);
    protected messages: ErrorMessages = {
        required: 'digipasSelector.error.required'
    };

    /**
     * Replacement for valuechanges subscription
     * Allows you to watch the value of the control as a signal
     */
    private readonly controlValue = toSignal(
        toObservable(this.control).pipe(switchMap((ctrl) => ctrl.valueChanges.pipe(startWith(this.control().value))))
    );

    /**
     * Type of selection allowed for user.
     * - `single` - user can select one account
     * - `multiple` - user can select one, multiple or all accounts
     */
    protected selectionType: ApSelectorSelectionType = 'single';

    /**
     * Watch value changes to update selected option
     */
    private setPreselectedValue(controlValue: Digipas): void {
        if (controlValue) {
            this.setSelectedOptionsFromValue(controlValue);
        }
    }

    /**
     * If only one option, select that option
     */
    private setSelectedOptions(selectorOptions: ApSelectorOption[]): void {
        // Select only option
        if (selectorOptions && selectorOptions.length === 1) {
            this.selectedOptions.set([selectorOptions[0]]);
            this.control()?.setValue(this.selectedOptions()[0].data);
        }
    }

    /**
     * Open selector modal with correct options
     */
    protected openModal(): void {
        const modalOptions = new ModalOptions<ApSelectorModalOptions>({
            options: this.selectorOptions(),
            selectedOptions: this.selectedOptions(),
            selectionType: this.selectionType
        });
        modalOptions.title = this.contentService.get('digipasSelector.label');

        const modalContainer = this.modalService.open(ApSelectorModalComponent, modalOptions);
        modalContainer.afterClosed().subscribe((result: ModalClosedResult<ApSelectorOption[]>) => {
            const control = this.control();
            if (control) {
                control.markAsDirty();
            }

            if (result.reason === ModalClosedReason.SELECTED) {
                this.selectedOptions.set(result.data);
                control?.setValue(this.selectedOptions()[0].data);
            }
        });
    }

    /**
     * Set selected options from passed value
     */
    private setSelectedOptionsFromValue(controlValue: Digipas): void {
        const selectorOptions = this.selectorOptions();
        if (!selectorOptions || !controlValue) {
            return;
        }
        const selectedId = this.generateOptionId(controlValue);
        this.selectedOptions.set(selectorOptions.filter((option) => selectedId === option.id));
    }

    /**
     * Map plain digipas to selector option
     */
    private mapSelectorOption(option: Digipas): ApSelectorOption {
        return {
            title: option.name,
            text: `${this.contentService.get('digipasSelector.number')}: ${option.number}`,
            id: this.generateOptionId(option),
            data: { ...option }
        };
    }

    /**
     * Generate unique id for option
     */
    private generateOptionId(option: Digipas): string {
        return `${this.contentService.get('digipasSelector.number')}-${option.number}`.replace(/\s+/g, '-').toLowerCase();
    }
}
